import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF_AND_DOC } from "@/components/upload/constants"
import useHandleImageFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { GoodsReceiptFileForm } from "@/screens/WarehouseReceiving/components/GoodsReceiptUploads/GoodsReceiptFileForm"
import type { GoodsReceiptFileArrayFormValues, GoodsReceiptFileFormValues } from "@/screens/WarehouseReceiving/schemas"
import { goodsReceiptFileArrayFormSchema } from "@/screens/WarehouseReceiving/schemas"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface GoodsReceiptFileFormDialogProps {
  onClose: () => void
  onRefresh: () => void
  goodsReceiptId: string
}

const BLANK_FILE: GoodsReceiptFileFormValues = {
  file: null,
  fileDescription: "",
  fileType: FieldGroupEnum.BillOfLading,
}

export const GoodsReceiptFileFormDialog: FC<GoodsReceiptFileFormDialogProps> = ({
  onClose,
  onRefresh,
  goodsReceiptId,
}) => {
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const methods = useForm<GoodsReceiptFileArrayFormValues>({
    resolver: zodResolver(goodsReceiptFileArrayFormSchema),
    defaultValues: {
      files: [BLANK_FILE],
    },
  })

  const { control, watch, formState } = methods
  const { isDirty, isSubmitting, isValid } = formState

  const {
    fields,
    append: appendNewFile,
    remove,
  } = useFieldArray({
    control,
    name: "files",
  })

  const files = watch("files")
  const showAddButton = files.every((field) => field.file !== null && !!field.fileType) && files.length < 5

  const { uploadFile } = useHandleImageFileUpload({
    referenceTableName: ReferenceTableName.AdvancedGoodsReceipt,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
  })

  const handleAddFile = (): void => {
    appendNewFile(BLANK_FILE)
  }

  const handleRemoveFile = (index: number): void => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  const canSubmit = !isSubmitting && isDirty && isValid

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return

    setIsUploading(true)

    try {
      const filesToUpload = files.filter(
        (
          fileData
        ): fileData is Required<Pick<GoodsReceiptFileFormValues, "file" | "fileType">> & GoodsReceiptFileFormValues =>
          !!fileData?.file && !!fileData?.fileType
      )

      await Promise.all(
        filesToUpload.map((fileData) =>
          uploadFile({
            file: fileData.file as File,
            type: fileData.fileType as FieldGroupEnum,
            referenceId: `${goodsReceiptId}`,
            label: fileData.fileDescription ?? "",
          })
        )
      )

      notify({
        message: "Files uploaded successfully",
        type: NotificationType.Success,
      })

      onRefresh()
      setIsUploading(false)
      onClose()
    } catch {
      setIsUploading(false)
      notify({
        message: "Failed to upload files",
        type: NotificationType.Error,
      })
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid='goods-receipt-file-dialog'>
      <DialogHeader title='Attach File' onClose={onClose} />
      <DialogContent classes={{ root: "p-10 pb-4" }}>
        <FormProvider {...methods}>
          <GoodsReceiptFileForm fields={fields} onRemove={handleRemoveFile} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button onClick={onClose} appearance='outlined'>
          Cancel
        </Button>
        <Box className='flex justify-end'>
          {showAddButton && (
            <Button startIcon={<AddOutlined />} appearance='text' onClick={handleAddFile}>
              Add Another File
            </Button>
          )}
          <Button type='submit' loading={isUploading} onClick={handleSubmit} disabled={!canSubmit}>
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
